import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface InlineErrorProps {
  /** What failed, in Swiftix terms — e.g. "We couldn't pull the cast list." */
  message: string;
  /** Retry handler. When omitted, no retry button renders. */
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  /** Compact variant for narrow rails and card slots. */
  compact?: boolean;
}

/**
 * Persistent, visible failure state with a retry affordance.
 * Use instead of `return null` / bare `console.error` so a failed fetch never
 * looks identical to a legitimately empty result.
 */
export const InlineError = ({
  message,
  onRetry,
  retryLabel = "Try again",
  className,
  compact = false,
}: InlineErrorProps) => (
  <div
    role="alert"
    className={cn(
      "flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5",
      compact ? "px-3 py-2.5" : "px-4 py-4 sm:px-5",
      className
    )}
  >
    <AlertTriangle className={cn("shrink-0 text-destructive", compact ? "h-4 w-4" : "h-5 w-5")} />
    <p className={cn("min-w-0 flex-1 text-foreground/80", compact ? "text-xs" : "text-sm")}>
      {message}
    </p>
    {onRetry && (
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="shrink-0 gap-1.5 border-destructive/40 hover:bg-destructive/10"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        {retryLabel}
      </Button>
    )}
  </div>
);
